import { find } from "lodash";
import styled from "styled-components";
import { SortArrow } from "../icons/SortArrow";
import { Flex } from "./Flex";
import { Scrollbar } from "./Scrollbar";
import { Label, Paragraph } from "./Text";

const StyledTableContainer = styled.div`
    height: calc(100% - 68px);
    background: #FFFFFF;
    box-shadow: 0px 16px 48px rgba(116, 151, 208, 0.1);
    border-radius: 20px;
    padding: 32px;
    box-sizing: border-box;
`;

export const TableContainer = (props) => <StyledTableContainer>{props.children}</StyledTableContainer>;

const StyledColumnTitle = styled.span`
    display: flex;
    align-items: center;
    height: 16px;
    cursor: pointer;

    & span {
        color: ${({sorted}) => sorted ? "var(--sp-main-color)" : "#8898B2"};
    }

    & span:not(:last-child) {
        margin-right: 4px;
    }
`;

const ColumnTitle = ({ sortOrder, sortColumn, dataIndex, title }) => {
    let sorted = sortColumn && sortColumn === dataIndex && sortOrder;
    return(
        <StyledColumnTitle sorted={sorted}>
            <span>{title}</span>
            {sorted ? <SortArrow rotate={ sortOrder === "ascend" ? 180 : 0 } /> : null}
        </StyledColumnTitle>
    )
};

const StyledTable = styled.table`
    width: 100%;
    display: grid;
    grid-template-columns: ${({gridTemplateColumns}) => gridTemplateColumns};

    & thead, & tbody, & tr {
        display: contents;
    }

    & th {
        position: relative;
        text-align: left;
        padding: 9px 12px;
        font-weight: 600;
        font-size: 12px;
        line-height: 16px;
        text-transform: uppercase;
        color: #8898B3;
        border-bottom: 2px solid #D7E3F7;
    }

    & tbody > tr:nth-child(even) > td {
        background: #F6F9FF;
    }

    & .table-placeholder {
        width: 100%;
        text-align: center;
    }

    & th > .resize-handler {
        position: absolute;
        top: 50%;
        right: 0;
        width: 1px;
        height: 1.6em;
        background-color: #D7E3F7;
        transform: translateY(-50%);
        transition: background-color .3s;
        cursor: col-resize;
    }
`;

const Cell = styled.td`
    padding: 13px;
    font-size: 14px;
    line-height: 22px;
    color: var(--sp-text-color);
    border: none;
    display: flex;
    align-items: center;

    overflow: ${({ ellipsis }) => ellipsis && "hidden"};
    text-overflow: ${({ ellipsis }) => ellipsis && "ellipsis"};
    white-space: ${({ ellipsis }) => ellipsis && "nowrap"};
`;

export const Table = ({
    columns=[],
    dataSource=[],
    sortOrder,
    sortColumn,
    setSorting,
    loading,
    emptyText
}) => {

    const onTableSort = (key) => {
        let col = find(columns, { dataIndex: key });
        if (col && col.sorter) {
            const sortOrders = [ null, "ascend", "descend" ];
            let sortIndex = key === sortColumn ? sortOrders.indexOf(sortOrder) + 1 : 1;
            if (sortIndex > 2) sortIndex = 0;
            setSorting({
                order: sortOrders[sortIndex],
                column: key
            });
        }
    };

    const renderContent = (data) => {
        return data.map((row, idx) => (
            <tr key={`row-${idx}`}>
                {columns.map((col, i) => (
                    <Cell key={`cell-${idx}-${i}`} ellipsis={typeof col.ellipsis === "boolean" ? col.ellipsis : true }>
                        {loading
                            ? <Paragraph isLoading lineHeight="22px" width="100%"/>
                            : col.render
                                ? col.render(row[col.dataIndex], row, i)
                                : row[col.dataIndex]}
                    </Cell>
                ))}
            </tr>
        ))
    };

    const sortData = (data) => {
        let col = find(columns, { dataIndex: sortColumn });
        if (col && col.sorter) {
            if (sortOrder === "ascend") {
                data = data.slice(0).sort((a,b) => col.sorter(a, b, sortColumn));
            } else if (sortOrder === "descend") {
                data = data.slice(0).sort((a,b) => col.sorter(a, b, sortColumn)).reverse();
            };
        }
        return data;
    };

    return(
        <div style={{ height: "100%", width: "100%", minHeight: "86px", borderBottom: "2px solid #D7E3F7"}}>
            <StyledTable id="tableHead" gridTemplateColumns={columns.map(col => col.width ? col.width + "px" : "1fr").join(" ")}>
                <thead>
                    <tr>
                        {columns.map((col, idx) => (
                            <th key={`col-header-${idx}`} onClick={() => onTableSort(col.dataIndex)}>
                                {col.sorter
                                    ? <ColumnTitle
                                        sortOrder={sortOrder}
                                        sortColumn={sortColumn}
                                        dataIndex={col.dataIndex}
                                        title={col.title}
                                    />
                                    : col.title
                                }
                            </th>
                        ))}
                    </tr>
                </thead>
            </StyledTable>
            {loading || dataSource.length
                ? <Scrollbar style={{ height: "calc(100% - 36px)" }}>
                    <StyledTable id="tableContent" gridTemplateColumns={columns.map(col => col.width ? col.width + "px" : "1fr").join(" ")}>
                        <tbody>
                            {renderContent(loading ? Array(20).fill(null) : sortData(dataSource))}
                        </tbody>
                    </StyledTable>
                </Scrollbar>
                : <Flex flex={1} justify="center" padding={{ top: "13px" }}>
                    <Label
                        bold
                        color="#D7E3F7"
                        size={emptyText && emptyText.size ? emptyText.size : "32px"}
                    >
                        {emptyText && emptyText.text ? emptyText.text : "No Data"}
                    </Label>
                </Flex>}
        </div>
    )
}